import { DragEvent, useState, useContext } from 'react'
import { getApolloContext } from '@apollo/react-common'
import { ShareListItemDragDataProps } from 'modules/shares/components/ShareListItemDrag'
import { readShareListItemFromCache } from 'modules/shares/operations/utils/share.cache'
import { ShareListItemFragment } from 'gqlTypes'

const VALID_DATATRANSFER_TYPES = ['Text', 'text/plain']

export interface ShareDropPayload {
  shareId: string
  share: ShareListItemFragment | undefined
}

export interface UseMenuShareDropProps {
  onDropped?: (data: ShareDropPayload) => void
}

export const useMenuShareDrop = ({ onDropped }: UseMenuShareDropProps) => {
  const [isDraggedOver, setDragged] = useState(false)
  const { client: apolloClient } = useContext(getApolloContext())
  const apolloCache = apolloClient?.cache

  if (!onDropped) {
    return {
      kind: 'disabled',
    } as const
  }

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    /*
    The dataTransfer types are expected from the dragged item, in this case the expected ones are "text",
    For this to work in IE11 we would have to use the type "text"
    https://stackoverflow.com/a/18051912

    IE 11 transforms "text" to "Text"
    and others from "text" to "text/plain" 🤦‍♂️
    */
    if (VALID_DATATRANSFER_TYPES.includes(event.dataTransfer.types[0])) {
      !isDraggedOver && setDragged(true)
    }
  }

  const onDragLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    setDragged(false)
  }

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()
    setDragged(false)

    const dataType = event.dataTransfer.types[0]

    const dataObject = JSON.parse(event.dataTransfer.getData(dataType)) as
      | ShareListItemDragDataProps
      | undefined
    const { shareId } = dataObject || {}

    if (!shareId) {
      return
    }

    let share: ShareListItemFragment | undefined
    if (apolloCache) {
      share = readShareListItemFromCache({ cache: apolloCache, id: shareId })
    }

    onDropped({ shareId, share })
  }

  return {
    kind: 'enabled',
    isDraggedOver,
    onDrop,
    onDragOver,
    onDragLeave,
  } as const
}
